import { httpClient } from '../lib/httpClient';
import { Note, NotesResponse, NoteCreateRequest } from '../types/notes';

export interface Client {
  id: string;
  name: string;
  email: string;
  phone?: string;
  type: string;
  jurisdiction: string;
  risk_profile?: any;  // TASK-179: JSONB object with section1-10 (investment risk questionnaire)
  is_active: boolean;
  status: string;  // TASK-178: 'prospect' or 'client'
  onboarding_completed_at?: string;  // TASK-178: When client completed onboarding
  created_at: string;
  updated_at: string;
  total_documents: number;
  last_upload_date?: string;
  classification?: {
    type?: string;
  };
  documents?: {
    totalDocuments: number;
  };
  metadata?: {
    meeting_notes?: any[];
    life_events?: any[];
    communications?: any[];
    extraction_history?: any[];
    profile_data?: {
      behavioral_insights?: {
        communication_style?: string;
        decision_pattern?: string;
        interests?: string[];
        concerns?: string[];
      };
      extraction_metadata?: {
        confidence_scores?: {
          demographics?: number;
        };
      };
    };
  };
  // NOTE: KYC data is stored in separate kyc_profiles table
  // Access via: kycService.profiles.get(clientId)
}

export interface ClientCreateData {
  name: string;
  email: string;
  phone?: string;
  type?: string;
  jurisdiction?: string;
  risk_profile?: string;
  is_prospect?: boolean;
}

// TASK-580: latest AI-generated narrative profile summary (ClientProfileSummary widget).
export interface ClientProfileSummaryData {
  client_id: string;
  sections: Record<string, string> | null; // 5 structured sections; null on legacy prose-only rows
  profile_summary: string | null; // null when scope_status === 'unverified' (TASK-656 fail-closed)
  confidence_score: number;
  documents_analyzed: number;
  generated_at: string | null;
  // TASK-656: fact-scope verdict — 'unverified' rows carry no content.
  scope_status?: 'unchecked' | 'verified' | 'unverified';
  scope_note?: string | null;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
  has_more: boolean;
}

export interface ExtractionHistory {
  client_id: string;
  total_extractions: number;
  extractions: Array<{
    id: string;
    upload_id: string;
    filename?: string;
    extraction_type: string;
    status: string;
    created_at: string;
    completed_at?: string;
    processing_time_ms?: number;
    error_message?: string;
  }>;
}

class ClientService {
  // TASK-086: Use httpClient instead of raw axios for authentication
  // (Removed axiosInstance in favor of httpClient)

  /**
   * Fetch all clients with pagination
   */
  // TASK-697: `active` came from the duplicate `services/api/clientService`, whose only consumer
  // was onboarding — and whose separate 5-minute cache meant onboarding could show a client every
  // other screen already showed as updated. Folded in here; that file is deleted.
  async fetchClients(offset = 0, limit = 20, filters?: { active?: boolean }): Promise<ClientListResponse> {
    try {
      const data = await httpClient.get<ClientListResponse>('/api/clients', {
        params: {
          offset,
          limit,
          ...(filters?.active !== undefined ? { active: filters.active } : {}),
        },
      });
      return data;
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  }

  /**
   * TASK-828 — the client's SUITABILITY state, from the server's own predicate.
   *
   * `risk_profile` here is `project_determined_profile(...)` projected to a flat string, or
   * `null` when the profile has no determined category — the same predicate the
   * `reprofile_due` detector and the agentic `detect_reprofile_triggers` tool read, and the
   * one TASK-619 bound to FinSA completeness.
   *
   * It is NOT the same question as "is the risk-profile form filled in", which the client
   * page computes for itself. Those two disagreed for any profile that was complete but never
   * produced a determined category, and neither said which question it answered.
   */
  async getComplianceSummary(clientId: string): Promise<{
    id: string;
    classification: string;
    /** `null` = no determined risk category on file. */
    risk_profile: string | null;
    is_us_person: boolean | null;
    is_pep: boolean | null;
  }> {
    return httpClient.get(`/api/clients/${clientId}/compliance-summary`);
  }

  /**
   * Get a single client by ID
   */
  async getClient(clientId: string): Promise<Client> {
    try {
      const data = await httpClient.get<Client>(`/api/clients/${clientId}`);
      return data;
    } catch (error) {
      console.error('Error fetching client:', error);
      throw error;
    }
  }

  /**
   * TASK-580: latest narrative profile summary (404 → throws; widget shows empty state)
   */
  async getLatestProfileSummary(clientId: string): Promise<ClientProfileSummaryData> {
    return httpClient.get<ClientProfileSummaryData>(`/api/client-profile/${clientId}/latest`);
  }

  /**
   * Get enriched client data with all extracted metadata
   */
  async getEnrichedClient(clientId: string): Promise<Client> {
    try {
      const data = await httpClient.get<Client>(`/api/clients/${clientId}/enriched`);
      return data;
    } catch (error) {
      console.error('Error fetching enriched client:', error);
      throw error;
    }
  }

  /**
   * Create a new client
   * TASK-178: Supports is_prospect parameter for prospect/client distinction
   */
  async createClient(clientData: {
    name: string;
    email: string;
    phone?: string;
    type?: string;
    jurisdiction?: string;
    risk_profile?: string;
    is_prospect?: boolean;  // TASK-178: Mark as prospect (default: false)
  }): Promise<Client> {
    try {
      const data = await httpClient.post<Client>('/api/clients', clientData);
      return data;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  }

  /**
   * Update client information
   */
  async updateClient(clientId: string, updates: Partial<Client>): Promise<Client> {
    try {
      const data = await httpClient.patch<Client>(`/api/clients/${clientId}`, updates);
      return data;
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  }

  /**
   * Delete a client (soft delete by default)
   */
  async deleteClient(clientId: string, softDelete = true): Promise<void> {
    try {
      await httpClient.delete(`/api/clients/${clientId}`, {
        params: { soft_delete: softDelete },
      });
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }

  /**
   * Upload a document for a client
   */
  async uploadDocument(clientId: string, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_id', clientId);

      const data = await httpClient.post<any>('/uploads/initialize', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Trigger extraction of client data from an uploaded document
   */
  async triggerExtraction(uploadId: string): Promise<any> {
    try {
      const data = await httpClient.post<any>(
        `/process/${uploadId}/extract-client-data`
      );
      return data;
    } catch (error) {
      console.error('Error triggering extraction:', error);
      throw error;
    }
  }

  /**
   * Get extraction history for a client
   */
  async getExtractionHistory(clientId: string): Promise<ExtractionHistory> {
    try {
      const data = await httpClient.get<ExtractionHistory>(
        `/api/clients/${clientId}/extraction-history`
      );
      return data;
    } catch (error) {
      console.error('Error fetching extraction history:', error);
      throw error;
    }
  }

  /**
   * Add a life event manually
   */
  async addLifeEvent(clientId: string, event: {
    date: string;
    type: string;
    description: string;
    financial_impact?: string;
  }): Promise<any> {
    try {
      const data = await httpClient.post<any>(
        `/api/clients/${clientId}/life-events`,
        event
      );
      return data;
    } catch (error) {
      console.error('Error adding life event:', error);
      throw error;
    }
  }

  /**
   * Get all communication notes for a client
   * TASK-122: Notes Tab Implementation
   */
  async getNotes(
    clientId: string,
    params?: {
      type?: string;
      start_date?: string;
      end_date?: string;
      search?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<NotesResponse> {
    try {
      const data = await httpClient.get<NotesResponse>(
        `/api/clients/${clientId}/notes`,
        { params }
      );
      return data;
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  }

  /**
   * Create a new communication note for a client
   * TASK-351: Frontend Note Creation Modal
   */
  async createNote(clientId: string, note: NoteCreateRequest): Promise<Note> {
    try {
      const data = await httpClient.post<Note>(
        `/api/clients/${clientId}/notes`,
        note
      );
      return data;
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  }

  /**
   * Search clients by name or email
   */
  async searchClients(query: string): Promise<Client[]> {
    try {
      const data = await httpClient.get<ClientListResponse>('/api/clients', {
        params: { search: query },
      });
      return data.clients;
    } catch (error) {
      console.error('Error searching clients:', error);
      throw error;
    }
  }

  /**
   * Get AI-generated insights for a client.
   * TASK-487: real per-client backend (data-ingestion). A client with no data returns an
   * honest empty state; InsightsTab already handles 404/empty.
   */
  async getInsights(clientId: string): Promise<import('../types/insights').ClientInsights> {
    return httpClient.get<import('../types/insights').ClientInsights>(
      `/api/clients/${clientId}/insights`
    );
  }

  /**
   * Trigger regeneration of AI insights for a client.
   * TASK-487: insights are computed live from current data, so this re-reads server-side.
   */
  async regenerateInsights(clientId: string): Promise<import('../types/insights').InsightsRefreshResponse> {
    return httpClient.post<import('../types/insights').InsightsRefreshResponse>(
      `/api/clients/${clientId}/insights/regenerate`
    );
  }
}

export const clientService = new ClientService();